import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PublishStatusBadge } from '@/components/publishing/PublishStatusBadge';
import type { ClipPostActions } from '@/hooks/use-clip-post-actions';
import { cn } from '@/lib/utils';

const stopCardClick = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

interface ClipPostMenuProps {
  post: ClipPostActions;
  /** Wrapper of the trigger; defaults to full width. */
  className?: string;
  triggerClassName?: string;
  /** Lets a narrow card hide the "Postar" text and chevron (the trigger keeps its name). */
  labelClassName?: string;
  chevronClassName?: string;
}

/** Polite per-platform live regions plus the Postar dropdown. */
export const ClipPostMenu: React.FC<ClipPostMenuProps> = ({
  post,
  className = 'w-full min-w-0',
  triggerClassName,
  labelClassName,
  chevronClassName,
}) => {
  const { t } = useTranslation();
  if (!post.showPostControls) return null;
  const { compact, actions, anyPublishing } = post;
  const iconClassName = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const defaultTriggerClassName = compact
    ? 'min-w-0 w-full h-auto min-h-11 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-tight'
    : 'w-full';

  return (
    <>
      {post.liveRegions.map((region) => (
        <div
          key={region.platform}
          data-testid={`${region.platform}-publish-live-region`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <PublishStatusBadge
            platform={region.platform}
            status={region.status}
            failReason={region.failReason}
          />
        </div>
      ))}
      {actions.length > 0 && (
        <div
          data-testid="clip-publishing-actions"
          className={className}
          onClick={stopCardClick}
        >
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                data-testid="post-menu-trigger"
                variant="secondary"
                size={compact ? 'sm' : undefined}
                className={triggerClassName ?? defaultTriggerClassName}
                aria-label={t('publishing.post')}
                aria-busy={anyPublishing}
                onClick={stopCardClick}
              >
                {anyPublishing ? (
                  <Loader2 className={`${iconClassName} animate-spin`} />
                ) : (
                  <Send className={iconClassName} />
                )}
                <span className={labelClassName}>{t('publishing.post')}</span>
                <ChevronDown className={cn(iconClassName, chevronClassName)} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-[100] min-w-[11rem]"
              onClick={stopCardClick}
            >
              {actions.map((action) => (
                <DropdownMenuItem
                  key={action.key}
                  data-testid={action.testId}
                  className="cursor-pointer gap-2"
                  disabled={action.disabled}
                  aria-busy={action.busy}
                  onSelect={(event) => {
                    event.preventDefault();
                    action.onSelect();
                  }}
                >
                  {action.icon}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );
};
